import React, { useState, useEffect, useRef } from 'react';
import { FileText, Edit } from 'lucide-react';
import { GenericListView, FormPageLayout, DocumentUpload, DisciplinePill, useToast } from '../common';
import Icd10Picker from '../common/Icd10Picker';
import type { Icd10Code } from '../../dtos/reference/icd10.dto';
import type { ActionButtonConfig, DocumentUploadDoc } from '../common';
import { ReferralsService, CreateReferralRequest, ReferralDocument } from '../../services/referrals.service';
import type { DeclineReason, ReferralStatus, ReferralStatusHistoryEntry } from '../../dtos/clinical/referral.dto';
import { usePatient } from '../../context/patientContext';
import { useHasAnyPermission } from '../../hooks/usePermission';
import {
  DECLINE_REASON_OPTIONS,
  MISSING_INFO_LABELS,
  REFERRAL_STATUS_LABELS,
  REFERRAL_TRANSITIONS,
  normalizeReferralStatus,
} from '../../constants/patientLifecycle';
import { notifyPatientChartUpdated } from '../../constants/navigationEvents';
import DeclineReferralDialog, { DeclineReferralInput } from './referral/DeclineReferralDialog';
import { disciplineCodes, disciplineReferralLabel } from '../../constants/disciplines';
import { useServicesOffered } from '../../hooks/useServicesOffered';

interface ReferralsProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  /** INACTIVE (discharged) patients are read-only (DSC-07) */
  readOnly?: boolean;
  /** Phase 20A (D-10): open a new referral for this discipline (Overview "Add") */
  newReferralDiscipline?: string;
}

interface Referral {
  referralId: string;
  referralNumber?: string;
  source: string;
  category: string;
  discipline?: string;
  referralDate: string;
  receivedDate: string;
  status: string;
  icd10Code?: string;
  referringProvider: string;
  referringNPI?: string;
  referringPhone?: string;
  referringFax?: string;
  referringAddressStreet?: string;
  referringAddressCity?: string;
  referringAddressState?: string;
  referringAddressZip?: string;
  referringSpecialty?: string;
  referringFacility?: string;
  contactPhone?: string;
  notes?: string;
  documents: (ReferralDocument | string)[];
  requestedSOCDate?: string;
  declineReason?: DeclineReason | null;
  declineNote?: string | null;
  statusHistory?: ReferralStatusHistoryEntry[];
  missingInfo?: string[];
}

/** Badge colors for referral statuses (compares the normalized uppercase value). */
export const getReferralStatusColor = (status: string): string => {
  switch (normalizeReferralStatus(status)) {
    case 'ACCEPTED': return 'bg-green-50 text-green-700 border border-green-200';
    case 'SCHEDULED': return 'bg-emerald-50 text-emerald-800 border border-emerald-200';
    case 'DECLINED': return 'bg-red-50 text-red-700 border border-red-200';
    case 'IN_REVIEW': return 'bg-blue-50 text-blue-700 border border-blue-200';
    default: return 'bg-gray-50 text-gray-700 border border-gray-200';
  }
};

const MISSING_INFO_BADGE = 'bg-amber-50 text-amber-800 border border-amber-300';

const formatTimestamp = (value: string) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const EMPTY_FORM: Partial<Referral> = {
  referralNumber: '',
  source: '',
  category: '',
  discipline: '',
  referralDate: '',
  receivedDate: '',
  status: 'RECEIVED',
  icd10Code: '',
  referringProvider: '',
  referringNPI: '',
  referringPhone: '',
  referringFax: '',
  referringAddressStreet: '',
  referringAddressCity: '',
  referringAddressState: '',
  referringAddressZip: '',
  referringSpecialty: '',
  referringFacility: '',
  contactPhone: '',
  notes: '',
  documents: [],
  requestedSOCDate: '',
};

const Referrals: React.FC<ReferralsProps> = ({ expanded, toggleExpanded, patientId, readOnly = false, newReferralDiscipline }) => {
  const servicesOffered = useServicesOffered();
  const { refreshDashboard } = usePatient();
  const { showToast } = useToast();
  
  // Permission checks for referrals
  const canCreateReferral = useHasAnyPermission([
    'patientmr:phi:referral:create' as any,
    'patientmr:phi:referral:createall' as any
  ]);

  const canReadReferral = useHasAnyPermission([
    'patientmr:phi:referral:read' as any,
    'patientmr:phi:referral:readall' as any
  ]);

  const canUpdateReferral = useHasAnyPermission([
    'patientmr:phi:referral:update' as any,
    'patientmr:phi:referral:updateall' as any
  ]);

  const canDeleteReferral = useHasAnyPermission([
    'patientmr:phi:referral:delete' as any,
    'patientmr:phi:referral:deleteall' as any
  ]);

  // Read-only mode: has read but no write permissions, or the patient is discharged (DSC-07)
  const isReadOnlyView = readOnly || (canReadReferral && !canCreateReferral && !canUpdateReferral && !canDeleteReferral);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Stored status of the referral being edited (drives the allowed next statuses) */
  const [originalStatus, setOriginalStatus] = useState<ReferralStatus>('RECEIVED');
  const [editingReferral, setEditingReferral] = useState<Referral | null>(null);
  const [statusNote, setStatusNote] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineBusy, setDeclineBusy] = useState(false);

  const [formData, setFormData] = useState<Partial<Referral>>(EMPTY_FORM);
  // The picked ICD-10 code with its description (the referral stores only the code)
  const [icd10Pick, setIcd10Pick] = useState<Icd10Code | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const hasLoadedRef = useRef<string | null>(null);

  const sourceOptions = ['Hospital', 'Physician', 'MCO', 'Family', 'Other'];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const categoryOptions = ['Nursing']; // PT/OT and ST will be added later

  // Discipline options (registry referral labels → codes), limited to the services the agency offers.
  // A referral already saved with a discipline that was later turned off keeps its label.
  const disciplineMap: Record<string, string> = Object.fromEntries(
    disciplineCodes({ includePlanned: true }).map(code => [disciplineReferralLabel(code), code])
  );
  const disciplineOptions = servicesOffered.map(code => disciplineReferralLabel(code));

  // Only the current status and its valid next statuses (§8 transition table). A new referral
  // starts as RECEIVED; DECLINED is only offered on an existing referral (it opens the decline dialog).
  const statusOptions = (editingId
    ? [originalStatus, ...REFERRAL_TRANSITIONS[originalStatus]]
    : (['RECEIVED', ...REFERRAL_TRANSITIONS.RECEIVED.filter((s) => s !== 'DECLINED')] as ReferralStatus[])
  ).map((value) => ({ value, label: REFERRAL_STATUS_LABELS[value] }));

  // Convert backend discipline code to display name
  const getDisplayName = (code: string): string => {
    const entry = Object.entries(disciplineMap).find(([_, v]) => v === code);
    return entry ? entry[0] : code;
  };

  /** Open the new-referral form, optionally with the requested service already chosen. */
  const openNewReferral = (disciplineCode?: string) => {
    setSelectedCategory('Nursing');
    setFormData((prev) => ({
      ...prev,
      category: 'Nursing',
      ...(disciplineCode ? { discipline: disciplineReferralLabel(disciplineCode) } : {}),
    }));
    setShowAddForm(true);
  };

  // Phase 20A (D-10): the Overview "Add" menu deep-links here with the discipline to order
  const handledNewRef = useRef<string | null>(null);
  useEffect(() => {
    if (!newReferralDiscipline || handledNewRef.current === newReferralDiscipline) return;
    if (!canCreateReferral || readOnly) return;
    handledNewRef.current = newReferralDiscipline;
    openNewReferral(newReferralDiscipline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newReferralDiscipline, canCreateReferral, readOnly]);

  const handleInputChange = (field: keyof Referral, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Upload the staged files to a saved referral through the pre-signed S3 flow (R-206), one at a
   * time. Returns the names and messages of the files that failed; the rest are saved.
   */
  const uploadStagedFiles = async (pid: string, referralId: string): Promise<string[]> => {
    const failures: string[] = [];
    for (const file of uploadedFiles) {
      try {
        await ReferralsService.uploadDocument(pid, referralId, file);
      } catch (err) {
        failures.push(`${file.name}: ${err instanceof Error ? err.message : 'upload failed'}`);
      }
    }
    return failures;
  };

  // Load referrals from API
  const loadReferrals = async (force = false) => {
    if (!patientId) return;
    
    // Prevent duplicate loads for the same patient
    if (!force && hasLoadedRef.current === patientId) {
      return;  // Already loaded this patient's referrals
    }
    
    setIsLoading(true);
    setError(null);
    try {
      const response = await ReferralsService.listReferrals(patientId);
      const mappedReferrals: Referral[] = response.referrals.map((ref) => {
        const provider = ref.referringProvider;
        const referringProviderName =
          typeof provider === 'string' ? provider : provider && typeof provider === 'object' ? provider.name || '' : '';
        return {
          referralId: ref.referralId || '',
          referralNumber: ref.referralNumber || '',
          source: ref.source,
          category: ref.category,
          discipline: ref.discipline,
          referralDate: ref.referralDate,
          receivedDate: ref.receivedDate,
          status: normalizeReferralStatus(ref.status),
          icd10Code: ref.icd10Code,
          referringProvider: referringProviderName,
          referringNPI: ref.referringNPI,
          referringPhone: ref.referringPhone,
          referringFax: ref.referringFax,
          referringAddressStreet: ref.referringAddressStreet,
          referringAddressCity: ref.referringAddressCity,
          referringAddressState: ref.referringAddressState,
          referringAddressZip: ref.referringAddressZip,
          referringSpecialty: ref.referringSpecialty,
          referringFacility: ref.referringFacility,
          contactPhone: ref.contactPhone,
          notes: ref.notes,
          documents: ref.documents || [],
          requestedSOCDate: ref.requestedSOCDate || '',
          declineReason: ref.declineReason ?? null,
          declineNote: ref.declineNote ?? null,
          statusHistory: ref.statusHistory || [],
          missingInfo: ref.missingInfo || [],
        };
      });
      setReferrals(mappedReferrals);
      hasLoadedRef.current = patientId;  // Mark as loaded
    } catch (err) {
      console.error('Error loading referrals:', err);
      setError(err instanceof Error ? err.message : 'Failed to load referrals');
      hasLoadedRef.current = patientId;  // Mark as loaded even on error to prevent retries
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) {
      loadReferrals();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loader is a plain function recreated every render; listing it would refetch on every render
  }, [expanded, patientId]);

  const handleCancel = () => {
    setShowAddForm(false);
    setSelectedCategory('');
    setEditingId(null);
    setEditingReferral(null);
    setOriginalStatus('RECEIVED');
    setStatusNote('');
    setFormData(EMPTY_FORM);
    setUploadedFiles([]);
  };

  /** Status select change: Declined opens the decline dialog instead of changing the field. */
  const handleStatusChange = (value: string) => {
    if (value === 'DECLINED') {
      setDeclineOpen(true);
      return;
    }
    handleInputChange('status', value);
  };

  const handleDecline = async (input: DeclineReferralInput) => {
    if (!patientId || !editingId) return;
    setDeclineBusy(true);
    try {
      await ReferralsService.updateReferral(patientId, editingId, {
        status: 'DECLINED',
        declineReason: input.declineReason,
        declineNote: input.declineNote,
      });
      setDeclineOpen(false);
      showToast('Referral declined.', { variant: 'success' });
      await loadReferrals(true);
      handleCancel();
      refreshDashboard?.();
      // Declining a pending shell's referral can make the patient inactive
      notifyPatientChartUpdated(patientId);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Unable to decline the referral.', { variant: 'error' });
    } finally {
      setDeclineBusy(false);
    }
  };

  const handleAddReferral = async () => {
    if (!patientId) {
      setError('Patient ID is required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Translate discipline display name to backend code
      const disciplineCode = formData.discipline
        ? disciplineMap[formData.discipline as keyof typeof disciplineMap] || formData.discipline
        : '';

      const referralRequest: CreateReferralRequest = {
        referralNumber: formData.referralNumber || '',
        source: formData.source || '',
        category: formData.category || '',
        discipline: disciplineCode,
        referralDate: formData.referralDate || '',
        receivedDate: formData.receivedDate || '',
        status: formData.status || 'RECEIVED',
        icd10Code: formData.icd10Code,
        referringProvider: formData.referringProvider?.trim() || undefined,
        referringNPI: formData.referringNPI,
        referringPhone: formData.referringPhone,
        referringFax: formData.referringFax,
        referringAddressStreet: formData.referringAddressStreet,
        referringAddressCity: formData.referringAddressCity,
        referringAddressState: formData.referringAddressState,
        referringAddressZip: formData.referringAddressZip,
        referringSpecialty: formData.referringSpecialty,
        referringFacility: formData.referringFacility,
        contactPhone: formData.contactPhone,
        notes: formData.notes,
        requestedSOCDate: formData.requestedSOCDate || undefined,
      };

      let savedReferralId: string;
      if (editingId) {
        const { status, ...rest } = referralRequest;
        const statusChanged = status !== originalStatus;
        // Existing documents the user removed: send the kept ones as references so the server
        // drops the rest. New files are not part of this payload; they upload after the save.
        const keptDocuments = (formData.documents || []).filter(
          (d): d is ReferralDocument => typeof d !== 'string' && !!d.documentId
        );
        const removedDocuments = (editingReferral?.documents || []).some(
          (d) => typeof d !== 'string' && !!d.documentId && !keptDocuments.some((k) => k.documentId === d.documentId)
        );
        await ReferralsService.updateReferral(patientId, editingId, {
          ...rest,
          // Only send a status when it changes, so an unchanged status is never a transition
          ...(statusChanged ? { status, ...(statusNote.trim() ? { statusNote: statusNote.trim() } : {}) } : {}),
          ...(removedDocuments
            ? { documents: keptDocuments.map((d) => ({ documentId: d.documentId, documentName: d.documentName })) }
            : {}),
        });
        savedReferralId = editingId;
      } else {
        const created = await ReferralsService.createReferral(patientId, referralRequest);
        savedReferralId = created.referralId || created.referral?.referralId || '';
      }

      // Documents go straight to S3 (pre-signed POST) once the referral exists
      if (uploadedFiles.length > 0) {
        const failures = savedReferralId
          ? await uploadStagedFiles(patientId, savedReferralId)
          : uploadedFiles.map((f) => `${f.name}: the referral id was not returned`);
        if (failures.length > 0) {
          showToast(
            `Referral saved, but ${failures.length} document(s) could not be uploaded. ${failures.join('; ')}`,
            { variant: 'error' }
          );
        }
      }

      await loadReferrals(true);
      notifyPatientChartUpdated(patientId);
      handleCancel();
      // Notify the overview dashboard so patientState/substate reflects the new referral
      refreshDashboard?.();
    } catch (err) {
      console.error('Error saving referral:', err);
      setError(err instanceof Error ? err.message : 'Failed to save referral');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditReferral = (referral: Referral) => {
    setEditingId(referral.referralId);
    setEditingReferral(referral);
    setOriginalStatus(normalizeReferralStatus(referral.status));
    setStatusNote('');
    setSelectedCategory(referral.category);
    setFormData({
      referralNumber: referral.referralNumber,
      source: referral.source,
      category: referral.category,
      // Convert backend discipline code to display name for form
      discipline: referral.discipline ? getDisplayName(referral.discipline) : '',
      referralDate: referral.referralDate,
      receivedDate: referral.receivedDate,
      status: referral.status,
      icd10Code: referral.icd10Code,
      referringProvider: referral.referringProvider,
      referringNPI: referral.referringNPI,
      referringPhone: referral.referringPhone,
      referringFax: referral.referringFax,
      referringAddressStreet: referral.referringAddressStreet,
      referringAddressCity: referral.referringAddressCity,
      referringAddressState: referral.referringAddressState,
      referringAddressZip: referral.referringAddressZip,
      referringSpecialty: referral.referringSpecialty,
      referringFacility: referral.referringFacility,
      contactPhone: referral.contactPhone,
      notes: referral.notes,
      documents: referral.documents,
      requestedSOCDate: referral.requestedSOCDate || '',
    });
    setUploadedFiles([]);
    setShowAddForm(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setFormData(prev => ({ ...prev, category }));
    setShowAddForm(true);
    setShowCategoryDropdown(false);
  };

  const handleDownloadDocument = async (doc: DocumentUploadDoc) => {
    if (!patientId || !editingId) return;
    try {
      const response = await ReferralsService.getDocumentSignedUrl(patientId, editingId, doc.id);
      const signedUrl = response.document.signedUrl;
      if (signedUrl) {
        window.open(signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      setError(error instanceof Error ? error.message : 'Failed to download document');
    }
  };

  const handleDeleteExistingDoc = (doc: DocumentUploadDoc) => {
    setFormData(prev => ({
      ...prev,
      documents: (prev.documents || []).filter(d => {
        if (typeof d === 'string') return true;
        return (d as ReferralDocument).documentId !== doc.id;
      }),
    }));
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = getReferralStatusColor;
  const statusLabel = (status: string) => REFERRAL_STATUS_LABELS[normalizeReferralStatus(status)];
  const declineReasonLabel = (reason?: string | null) =>
    DECLINE_REASON_OPTIONS.find((o) => o.value === reason)?.label ?? reason ?? '';
  const missingInfoText = (missing?: string[]) =>
    (missing || []).map((key) => MISSING_INFO_LABELS[key] ?? key).join(', ');
  const isTerminal = REFERRAL_TRANSITIONS[originalStatus].length === 0;

  // Map existing referral docs to DocumentUploadDoc for the DocumentUpload component
  const existingDocs: DocumentUploadDoc[] = (formData.documents || [])
    .filter((d): d is ReferralDocument => typeof d !== 'string')
    .map(d => ({ id: d.documentId, name: d.documentName || 'Unknown' }));

  const formActions: ActionButtonConfig[] = [
    {
      key: 'cancel',
      label: 'Cancel',
      variant: 'secondary',
      onClick: handleCancel,
      disabled: isLoading,
    },
    {
      key: 'save',
      label: editingId ? 'Update Referral' : 'Add Referral',
      loadingLabel: 'Saving...',
      variant: 'primary',
      onClick: handleAddReferral,
      loading: isLoading,
      disabled:
        !formData.source ||
        !formData.referralDate ||
        !formData.receivedDate ||
        isLoading,
      // Hide save button in read-only mode
      hidden: isReadOnlyView,
    },
  ];

  const sortedReferrals = [...referrals].sort(
    (a, b) => new Date(b.receivedDate).getTime() - new Date(a.receivedDate).getTime()
  );

  // ── Render: Form ────────────────────────────────────────────────────────────

  if (showAddForm) {
    // Case C: User lacks read permissions - prevent form access
    if (!canReadReferral) {
      return (
        <FormPageLayout
          title={editingId ? 'Edit Referral' : 'New Referral'}
          onBack={handleCancel}
          actions={[]}
        >
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-center max-w-md">
              <div className="text-red-500 text-4xl mb-4">🔒</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Not enough permissions</h3>
              <p className="text-gray-600 text-sm">
                You don't have the required permissions to view referral forms. Contact your administrator if you need access.
              </p>
            </div>
          </div>
        </FormPageLayout>
      );
    }

    return (
      <FormPageLayout
        title={editingId ? 'Edit Referral' : 'New Referral'}
        onBack={handleCancel}
        actions={formActions}
      >
        {/* Read-only mode notification */}
        {isReadOnlyView && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>View Only:</strong> You have permission to view this referral, but cannot make changes.
            </p>
          </div>
        )}
        {editingReferral && (editingReferral.missingInfo?.length ?? 0) > 0 && (
          <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50 text-sm text-amber-900">
            <strong>Missing information:</strong> {missingInfoText(editingReferral.missingInfo)}
          </div>
        )}
        {editingReferral && originalStatus === 'DECLINED' && editingReferral.declineReason && (
          <div className="mb-4 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-800">
            <strong>Declined:</strong> {declineReasonLabel(editingReferral.declineReason)}
            {editingReferral.declineNote ? ` — ${editingReferral.declineNote}` : ''}
          </div>
        )}
        {/* Referral Details Section */}
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-300">Referral Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-left">

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Referral Source <span className="text-red-500">*</span>
              </label>
              <select
                disabled={isReadOnlyView}
                value={formData.source}
                onChange={(e) => handleInputChange('source', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 bg-white ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
              >
                <option value="">Select source...</option>
                {sourceOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Referral Number</label>
              <input
                type="text"
                disabled={isReadOnlyView}
                value={formData.referralNumber || ''}
                onChange={(e) => handleInputChange('referralNumber', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="Enter referral number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Referral Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                disabled={isReadOnlyView}
                value={formData.referralDate}
                onChange={(e) => handleInputChange('referralDate', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Received Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                disabled={isReadOnlyView}
                value={formData.receivedDate}
                onChange={(e) => handleInputChange('receivedDate', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                disabled={isReadOnlyView || (!!editingId && isTerminal)}
                value={formData.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 bg-white ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {editingId && isTerminal && (
                <p className="mt-1 text-xs text-slate-500">{statusLabel(originalStatus)} is a final status.</p>
              )}
            </div>

            {editingId && formData.status !== originalStatus && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status change note</label>
                <input
                  type="text"
                  disabled={isReadOnlyView}
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="Optional"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Requested Start of Care</label>
              <input
                type="date"
                disabled={isReadOnlyView}
                value={formData.requestedSOCDate || ''}
                onChange={(e) => handleInputChange('requestedSOCDate', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
              />
            </div>

            <div>
              <label htmlFor="referral-icd10" className="block text-sm font-medium text-slate-700 mb-1">ICD-10 Diagnostic Code</label>
              <Icd10Picker
                id="referral-icd10"
                value={
                  icd10Pick && icd10Pick.code === formData.icd10Code
                    ? icd10Pick
                    : formData.icd10Code ? { code: formData.icd10Code, description: '', billable: true } : null
                }
                onChange={(code) => {
                  setIcd10Pick(code);
                  handleInputChange('icd10Code', code?.code || '');
                }}
                placeholder="Search by code or description (optional)"
                disabled={isReadOnlyView}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Discipline</label>
              <select
                disabled={isReadOnlyView}
                value={formData.discipline}
                onChange={(e) => handleInputChange('discipline', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 bg-white ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
              >
                <option value="">Select discipline...</option>
                {disciplineOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

          </div>
        </div>

        {/* Referring Physician Info Section */}
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-300">Referring Physician Info</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name
              </label>
              <input
                type="text"
                disabled={isReadOnlyView}
                value={formData.referringProvider}
                onChange={(e) => handleInputChange('referringProvider', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="Enter provider name"
              />
              {!formData.referringProvider?.trim() && (
                <p className="mt-1 text-xs text-slate-500">Optional. Without a physician the referral is flagged "Missing information".</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">NPI Number</label>
              <input
                type="text"
                disabled={isReadOnlyView}
                value={formData.referringNPI}
                onChange={(e) => handleInputChange('referringNPI', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="Enter NPI number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <input
                type="tel"
                disabled={isReadOnlyView}
                value={formData.referringPhone}
                onChange={(e) => handleInputChange('referringPhone', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="555-0100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fax</label>
              <input
                type="tel"
                disabled={isReadOnlyView}
                value={formData.referringFax}
                onChange={(e) => handleInputChange('referringFax', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="555-0200"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Address (Street)</label>
              <input
                type="text"
                disabled={isReadOnlyView}
                value={formData.referringAddressStreet}
                onChange={(e) => handleInputChange('referringAddressStreet', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="Street address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
              <input
                type="text"
                disabled={isReadOnlyView}
                value={formData.referringAddressCity}
                onChange={(e) => handleInputChange('referringAddressCity', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="City"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
              <input
                type="text"
                disabled={isReadOnlyView}
                value={formData.referringAddressState}
                onChange={(e) => handleInputChange('referringAddressState', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="State"
                maxLength={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Zip</label>
              <input
                type="text"
                disabled={isReadOnlyView}
                value={formData.referringAddressZip}
                onChange={(e) => handleInputChange('referringAddressZip', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="Zip code"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Specialty</label>
              <input
                type="text"
                disabled={isReadOnlyView}
                value={formData.referringSpecialty}
                onChange={(e) => handleInputChange('referringSpecialty', e.target.value)}
                className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
                  isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="e.g., Cardiology"
              />
            </div>

          </div>
        </div>

        {/* Additional Notes Section */}
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-300">Additional Notes</h4>
          <textarea
            disabled={isReadOnlyView}
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 ${
              isReadOnlyView ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
            }`}
            placeholder="Add any additional notes..."
          />
        </div>

        {/* Documents */}
        <DocumentUpload
          collapsible
          defaultOpen
          canUpload={!isReadOnlyView}
          accept=".pdf,.jpg,.jpeg,.png"
          maxSizeMb={10}
          documents={existingDocs}
          stagedFiles={uploadedFiles}
          uploading={isLoading && uploadedFiles.length > 0}
          onFilesSelected={(files) => setUploadedFiles(prev => [...prev, ...files])}
          onStagedRemove={(i) => setUploadedFiles(prev => prev.filter((_, idx) => idx !== i))}
          onDownload={editingId ? handleDownloadDocument : undefined}
          onDelete={handleDeleteExistingDoc}
          onValidationError={(msg) => setError(msg)}
        />

        {editingReferral && (
          <div>
            <h4 className="text-sm font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-300">Status history</h4>
            {(editingReferral.statusHistory?.length ?? 0) === 0 ? (
              <p className="text-sm text-slate-500 text-left">No status changes recorded yet.</p>
            ) : (
              <ol className="relative border-l border-slate-300 ml-2 text-left" aria-label="Referral status history">
                {[...(editingReferral.statusHistory || [])]
                  .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
                  .map((entry, index) => (
                    <li key={`${entry.at}-${index}`} className="mb-3 ml-4">
                      <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white bg-slate-400" aria-hidden="true" />
                      <p className="text-sm text-slate-800">
                        {entry.from ? `${statusLabel(entry.from)} → ` : ''}
                        <span className="font-medium">{statusLabel(entry.to)}</span>
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatTimestamp(entry.at)}
                        {entry.by ? ` · ${entry.by}` : ''}
                      </p>
                      {entry.note && <p className="text-xs text-slate-600 mt-0.5">{entry.note}</p>}
                    </li>
                  ))}
              </ol>
            )}
          </div>
        )}

        <DeclineReferralDialog
          open={declineOpen}
          busy={declineBusy}
          onConfirm={handleDecline}
          onCancel={() => setDeclineOpen(false)}
        />
      </FormPageLayout>
    );
  }

  // ── Render: List ─────────────────────────────────────────────────────────────

  // Case C: User lacks read permissions - show permission error
  if (expanded && !canReadReferral) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-4xl mb-4">🔒</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Not enough permissions to view referrals</h3>
          <p className="text-gray-600 text-sm">
            You don't have the required permissions to view referrals. Contact your administrator if you need access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <GenericListView
      title="Referrals"
      icon={<FileText size={20} />}
      expanded={expanded}
      onToggleExpand={toggleExpanded}
      items={sortedReferrals}
      keyExtractor={(referral) => referral.referralId}
      columns={[
        {
          key: 'referralNumber',
          label: 'Ref #',
          width: 'w-24',
          render: (referral) => (
            <span className="text-sm font-medium text-gray-900">
              {referral.referralNumber || '-'}
            </span>
          ),
        },
        {
          key: 'provider',
          label: 'Provider',
          width: 'flex-1 min-w-0',
          render: (referral) => (
            <div>
              <span className="text-sm text-gray-900">{referral.referringProvider || <span className="italic text-gray-400">No physician</span>}</span>
              {referral.referringFacility && (
                <div className="text-xs text-gray-500">{referral.referringFacility}</div>
              )}
            </div>
          ),
        },
        {
          key: 'discipline',
          label: 'Discipline',
          width: 'w-40',
          align: 'center',
          render: (referral) => (
            referral.discipline ? <DisciplinePill discipline={referral.discipline} /> : <span className="text-gray-400">—</span>
          ),
        },
        {
          key: 'receivedDate',
          label: 'Received',
          width: 'w-32',
          render: (referral) => (
            <span className="text-sm font-medium text-gray-900">
              {formatDate(referral.receivedDate)}
            </span>
          ),
        },
        {
          key: 'status',
          label: 'Status',
          width: 'w-28',
          align: 'center',
          render: (referral) => (
            <div className="flex flex-col items-center gap-1">
              <span className={`px-3 py-1 rounded text-xs font-semibold ${getStatusColor(referral.status)}`}>
                {statusLabel(referral.status)}
              </span>
              {(referral.missingInfo?.length ?? 0) > 0 && (
                <span
                  className={`px-2 py-0.5 rounded text-[11px] font-semibold ${MISSING_INFO_BADGE}`}
                  title={`Missing: ${missingInfoText(referral.missingInfo)}`}
                >
                  Missing information
                </span>
              )}
            </div>
          ),
        },
      ]}
      actions={[
        {
          icon: <Edit size={14} />,
          label: isReadOnlyView ? 'View referral' : 'Edit referral',
          onClick: (referral: Referral) => handleEditReferral(referral),
          isVisible: () => canReadReferral,
          className: isReadOnlyView
            ? 'p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors'
            : 'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors',
        },
      ]}
      mobileCard={{
        primaryText: (referral) => referral.referringProvider || referral.source || 'Referral',
        secondaryText: (referral) => formatDate(referral.receivedDate),
        badges: (referral) =>
          [
            { label: referral.discipline || '-', className: 'bg-blue-50 text-blue-700 border border-blue-200' },
            { label: statusLabel(referral.status), className: getStatusColor(referral.status) },
            ...((referral.missingInfo?.length ?? 0) > 0 ? [{ label: 'Missing information', className: MISSING_INFO_BADGE }] : []),
          ].filter((badge) => badge.label && badge.label !== '-'),
        actions: (referral) => [
          {
            icon: <Edit size={18} />,
            label: isReadOnlyView ? 'View referral' : 'Edit referral',
            onClick: () => handleEditReferral(referral),
            className: isReadOnlyView
              ? 'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors'
              : 'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors',
          },
        ],
      }}
      onRefresh={() => loadReferrals(true)}
      refreshing={false}
      loading={isLoading}
      newButtonLabel="New Referral"
      onNewClick={() => openNewReferral()}
      showNew={canCreateReferral && !readOnly}
      error={error}
      onDismissError={() => setError(null)}
      hasLoadedOnce={true}
      emptyStateIcon={<FileText size={48} className="text-gray-300" />}
      emptyStateTitle="No referrals available"
      emptyStateMessage={canCreateReferral ? "Start by creating a new referral using the button above." : "You don't have permission to create referrals."}
    />
  );
};

export default Referrals;
